import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm, Controller, FormProvider } from 'react-hook-form';

// UTILS
import { useQuery } from '../../util/hooks';
import { AppThunkDispatch } from '../../util/store';
import getConstants from '../../util/constants';
import { useAppStepNavigation } from '../../util/hooks/useAppStepNavigation';

// REDUX AND SLICES
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { loginWithKnownAuthToken, fetchMerchantData } from '../../reducers/auth-slice';
import {
  setCustomerDetails,
  fetchExistingData,
  customerSearch,
  saveCustomerDetails,
  setCustomerId,
  resetCustomerMatch,
} from '../../reducers/customer-slice';
import {
  updateCheckout,
  fetchCheckoutAttributes,
  attachCustomer,
  runPlanPreApprovalCheck,
} from '../../reducers/checkout-slice';

// WEB COMPONENTS
import {
  Alert,
  Button,
  IconAttention,
  IconNext,
  PaymentCalculator,
  PaymentCalculatorData,
  fixNameCase,
} from '@payright/web-components';

// FORM COMPONENTS
import { EditDetailsBlock } from '../../components/customer-data/edit-details-block';
import {
  CustomerDetails,
  CustomerDetailsData,
} from '../../components/customer-data/customer-details';
import {
  AddressDetails,
  AddressDetailsData,
  ShippingDetailsData,
} from '../../components/customer-data/address-details';

import EmploymentDetails, {
  EmploymentDetailsData,
} from '../../components/customer-data/employment-details';
import {
  IdentificationDetails,
  IdentificationDetailsData,
  IdentificationType,
} from '../../components/customer-data/identification-details';
import { FeeDetails } from '../../components/fee-details';

// OTHER COMPONENTS
import { StepBody } from '../../components/step-body';
import SCSpinner from '../../components/spinner';
import Loader from '../../components/loader';
import { ButtonCancelPlan } from '../../components/buttons/ButtonCancelPlan';

// LAYOUT COMPONENTS
import LargerHeaderWithNavLayout from '../../layouts/large-header-with-nav-layout';

// STYLES AND STYLED COMPONENTS
import { SCEcommerceNewPlan } from './css';

// EVENT HANDLERS
import { ApplicationCompletedBy, CheckoutStep } from '@payright/types-bnpl-checkout-api';
import type { CustomerResource } from '@payright/types-bnpl-checkout-api';
import { EXISTING_CUSTOMER_ERROR } from '../../util/constants/global';
import { LinkEcommerceLogin } from '../../components/links/LinkEcommerceLogin';
import { AlertSliceError } from '../../components/alert-slice-error';

const {
  PAYMENT_FREQUENCIES,
  REDIRECT_URL,
  ADDRESS_DETAIL_TEXT_NEW_CUSTOMER,
  ADDRESS_DETAIL_TEXT_EXISTING_CUSTOMER,
  CUSTOMER_IDENTIFICATION,
  CUSTOMER_DETAILS_CUSTOMER_INSTRUCTIONS,
} = getConstants();

type FormData = {
  customerDetails: CustomerDetailsData;
  addressDetails: AddressDetailsData;
  shippingDetails: ShippingDetailsData;
  employmentDetails: EmploymentDetailsData;
  identificationDetails: IdentificationDetailsData;
  paymentDetails: PaymentCalculatorData;
  customerNumber: string;
};

const Step1 = () => {
  const { goToStep } = useAppStepNavigation();
  const location = useLocation();
  const dispatch = useDispatch<AppThunkDispatch>();

  // URL QUERY PARAMS
  const query = useQuery();
  const queryParamCheckoutId = query.get('checkoutId');

  // GLOBAL STATE
  const globalAuthState = useSelector((state: RootState) => state.auth);
  const globalCustomerState = useSelector((state: RootState) => state.customer);
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);

  // VARIABLES
  const merchantData = globalAuthState.merchantRates;
  const applicationCompleteBy = globalCheckoutState.checkout?.applicationCompletedBy || '';

  const canCustomerProceed =
    globalCustomerState.existingCustomerState?.customerSearchResponse?.canCustomerProceed;
  const customerMatch = globalCustomerState.customerMatch || false;
  const hasCustomerErrors: boolean = globalCustomerState.hasErrors;
  const hasCheckoutErrors: boolean = globalCheckoutState.hasErrors;
  const customerSearchErrors =
    globalCustomerState.existingCustomerState?.customerSearchResponse?.errors;

  const email = globalCustomerState.customerDetails?.email;
  const password = globalCustomerState.customerDetails?.password || '';
  const idType = globalCustomerState.identificationDetails?.identificationType;
  const repeatCustomer = globalCustomerState?.accountDetails?.isReturnCustomer;
  const checkout = globalCheckoutState.checkout;
  const checkoutId = globalCheckoutState.checkout?.id || '';
  const redirectUrl = globalCheckoutState.checkout?.attributes?.redirectUrl || REDIRECT_URL;

  const authLoading = globalAuthState.loading;
  const checkoutLoading = globalCheckoutState.loading;
  const customerLoading = globalCustomerState.loading;

  const isExistingCustomer = globalAuthState.isLoggedIn;
  const isCustomerLed = applicationCompleteBy === 'Customer';

  const isIdVerified = globalCustomerState.customerDetails?.isIdVerified || false;

  // LOCAL STATE
  const [identificationType, setIdentificationType] = useState<IdentificationType>('license');
  const [isCustomerFormReadonly, setIsCustomerFormReadonly] = useState(false);

  // Todo: Refactor these states to use a single object instead
  const [isShippingAddressSame, setIsShippingAddressSame] = useState<boolean>(true);

  // REFS TO SCROLL TO
  const customerSearchRef = useRef<HTMLInputElement>(null);
  const hiddenBlockRef = useRef<HTMLInputElement>(null);

  const showAllAvailableTerms = globalCheckoutState.checkout?.attributes?.showAllAvailableTerms;

  useEffect(() => {
    if (
      typeof globalCustomerState.shippingDetails !== 'undefined' &&
      typeof globalCustomerState.addressDetails !== 'undefined' &&
      globalCustomerState.shippingDetails.street === globalCustomerState.addressDetails.street
    ) {
      setIsShippingAddressSame(true);
    } else if (
      typeof globalCustomerState.shippingDetails !== 'undefined' &&
      globalCustomerState.shippingDetails.street === ''
    ) {
      // Customer doesn't have shipping address
      setIsShippingAddressSame(true);
    } else {
      setIsShippingAddressSame(false);
    }
  }, [
    isCustomerLed,
    isExistingCustomer,
    globalCustomerState.employmentDetails,
    globalCustomerState.addressDetails,
    globalCustomerState.identificationDetails,
  ]);

  /**
   * The page is split into 2 distinct sections/steps. Section #1 is displayed first, and as the user steps through the page,
   * section #1 becomes readonly, and section #2 displays. When the user proceeds to the next page, and presses back on their browser,
   * we need to send the user back to section #1 (editable) instead of section #2. This "hack" detects for changes in the URL and resets
   * the state accordingly
   */
  useEffect(() => {
    dispatch(resetCustomerMatch());
  }, [location, dispatch]);

  // THE FORM IS BROKEN DOWN INTO 2 COMPONENTS
  // 1. The payment summary and customer block
  // 2. The customer details (address, employment, identification) block

  //////////  START OF THE PAYMENT AND CUSTOMER FORM BLOCK //////////
  const reactHookFormCustomerIdentification = useForm<FormData>({
    defaultValues: {
      paymentDetails: {
        ...globalCheckoutState.paymentDetails,
        paymentPeriod: globalAuthState.storeConfig?.defaultTerm,
      },
      customerDetails: { ...globalCustomerState.customerDetails },
    },
    shouldFocusError: false,
  });

  const {
    handleSubmit: handleSubmitCustomer,
    setValue: setValueCustomer,
    watch,
  } = reactHookFormCustomerIdentification;

  const selectedPaymentFrequency = watch('paymentDetails').paymentFrequency;

  // CUSTOMER FORM HANDLER
  const formSubmitCustomerHandler = (formData: FormData) => {
    setIsCustomerFormReadonly(true);
    dispatch(
      updateCheckout({
        checkoutIdentifier: queryParamCheckoutId || '',
        paymentDetails: formData.paymentDetails,
        checkoutData: {
          type: checkout?.attributes?.type,
          redirectUrl: checkout?.attributes?.redirectUrl,
          expiresAt: checkout?.attributes?.expiresAt,
          step: CheckoutStep.CHECKOUT_STEP_IN_PROGRESS,
        },
      })
    )
      .then(() => {
        dispatch(
          setCustomerDetails({
            isExistingCustomer,
            ...formData,
          })
        );
        const saleAmount = formData.paymentDetails?.saleAmount || 0;
        const depositAmount = formData.paymentDetails?.depositAmount || 0;
        const loanAmount = saleAmount - depositAmount;
        dispatch(
          customerSearch(
            queryParamCheckoutId || '',
            formData.customerDetails,
            isExistingCustomer ? 'existingCustomer' : 'newCustomer',
            loanAmount
          )
        );
      })
      .catch((e) => {
        setIsCustomerFormReadonly(false);
        throw e;
      });
  };

  // UPDATE FORM VALUES WHEN REDUX STATE CHANGES
  useEffect(() => {
    if (globalCheckoutState.paymentDetails) {
      setValueCustomer('paymentDetails', globalCheckoutState.paymentDetails);
    }
  }, [globalCheckoutState.paymentDetails, checkoutLoading, setValueCustomer]);

  useEffect(() => {
    if (globalCustomerState.customerDetails) {
      setValueCustomer('customerDetails', globalCustomerState.customerDetails);
    }
  }, [globalCustomerState.customerDetails, setValueCustomer]);
  //////////  END OF THE PAYMENT AND CUSTOMER FORM BLOCK //////////

  //////////  START OF THE CUSTOMER DETAILS FORM BLOCK //////////
  const reactHookFormDetails = useForm<FormData>({
    defaultValues: {
      addressDetails: { ...globalCustomerState.addressDetails },
      shippingDetails: { ...globalCustomerState.shippingDetails },
      employmentDetails: { ...globalCustomerState.employmentDetails },
      identificationDetails: { ...globalCustomerState.identificationDetails },
    },
    shouldFocusError: true,
    shouldUnregister: false,
  });

  const { handleSubmit, setValue, clearErrors, reset, getValues } = reactHookFormDetails;

  const ref = useRef(true);

  useEffect(() => {
    // TODO DatePicker using onChange causing isDirty = true. Temp fix below.
    // TODO Load once, if customer-details component loaded, but upon forms init, isDirty init is true
    // console.log('form', reactHookFormDetails.formState.dirtyFields); // TODO DatePicker using onChange causing isDirty = true

    // Only for new customer, as they have zero identification details initialised
    if (globalCustomerState.identificationDetails) {
      // Proceed with initialising temporary hotfix
      const formsUsingDatePickerBug =
        globalCustomerState.identificationDetails.identificationType === 'license' ||
        globalCustomerState.identificationDetails.identificationType === 'passport';

      if (reactHookFormDetails.formState.isDirty && formsUsingDatePickerBug && ref.current) {
        reset(
          {
            ...getValues(),
          },
          { isDirty: false }
        );
        ref.current = false;
      } else {
        console.log('form updated', reactHookFormDetails.formState.isDirty); // DO NOT REMOVE!!!!
      }
    }
  }, [reactHookFormDetails.formState.isDirty]);

  const planPreApproval = () => {
    if (!queryParamCheckoutId) return;

    dispatch(
      runPlanPreApprovalCheck({
        checkoutId: queryParamCheckoutId,
        isCheckoutUpdated: reactHookFormDetails.formState.isDirty,
        isExistingCustomer: isExistingCustomer,
        onSuccessHandleRedirection: (result) => goToStep(result.nextStep),
      })
    );
  };

  // CUSTOMER DETAILS FORM HANDLER
  const formSubmitDetailsHandler = (formData: FormData) => {
    const fixedName = fixNameCase(
      globalCustomerState.customerDetails.firstName,
      globalCustomerState.customerDetails.middleName,
      globalCustomerState.customerDetails.lastName
    );

    if (isShippingAddressSame) {
      formData.shippingDetails = formData.addressDetails;
    }

    dispatch(
      setCustomerDetails({
        isExistingCustomer,
        ...formData,
      })
    );

    const customer: CustomerResource = {
      ...formData,
      id: globalAuthState.customerId || null,
      customerDetails: {
        ...(globalCustomerState.customerDetails || undefined),
        firstName: fixedName.firstName,
        middleName: fixedName.middleName,
        lastName: fixedName.lastName,
      },
    };

    // TODO: Error handling, and check if customer is already linked
    if (queryParamCheckoutId) {
      dispatch(saveCustomerDetails(customer, queryParamCheckoutId)).then(
        (customerSaveResult: any) => {
          dispatch(
            attachCustomer(
              queryParamCheckoutId,
              customerSaveResult.id as string,
              ApplicationCompletedBy.ECOMMERCE
            )
          )
            .then(async () => {
              //TODO - move `localStorage.setItem` to customer reducer
              localStorage.setItem('customerId', customerSaveResult.id);
              dispatch(setCustomerId({ customerId: customerSaveResult.id }));

              if (customerSaveResult.auth_token) {
                dispatch(
                  loginWithKnownAuthToken(
                    customerSaveResult.auth_token,
                    customerSaveResult.id,
                    customerSaveResult.email
                  )
                );
              }

              planPreApproval();
            })
            .catch((err) => {
              // attachCustomer error - Main error displayed to user
              console.log(err);
            });
        }
      );
    }
  };

  // UPDATE FORM VALUES WHEN REDUX STATE CHANGES
  useEffect(() => {
    if (globalCustomerState.addressDetails) {
      Object.keys(globalCustomerState.addressDetails).forEach((key) => {
        clearErrors(`addressDetails.${key}`);
        setValue(`addressDetails.${key}`, Reflect.get(globalCustomerState.addressDetails, key));
      });
    }

    if (globalCustomerState.shippingDetails) {
      Object.keys(globalCustomerState.shippingDetails).forEach((key) => {
        clearErrors(`shippingDetails.${key}`);
        setValue(`shippingDetails.${key}`, Reflect.get(globalCustomerState.shippingDetails, key));
      });
    }

    if (idType !== null) {
      setIdentificationType(idType);
    }

    if (globalCustomerState.employmentDetails) {
      Object.keys(globalCustomerState.employmentDetails).forEach((key) => {
        setValue(
          `employmentDetails.${key}`,
          Reflect.get(globalCustomerState.employmentDetails, key)
        );
      });
    }

    if (globalCustomerState.identificationDetails) {
      Object.keys(globalCustomerState.identificationDetails).forEach((key) => {
        clearErrors(`identificationDetails.${key}`);

        setValue(
          `identificationDetails.${key}`,
          Reflect.get(globalCustomerState.identificationDetails, key)
        );
      });
    }
  }, [
    globalCustomerState.addressDetails,
    globalCustomerState.shippingDetails,
    globalCustomerState.employmentDetails,
    globalCustomerState.identificationDetails,
    idType,
    setValue,
    setIdentificationType,
    clearErrors,
  ]);
  ////////// END OF THE CUSTOMER DETAILS FORM BLOCK //////////

  // LOAD LATEST CHECKOUT FROM THE SERVER
  useEffect(() => {
    if (queryParamCheckoutId !== null && queryParamCheckoutId !== checkoutId) {
      dispatch(fetchMerchantData(queryParamCheckoutId)).then(() => {
        dispatch(fetchCheckoutAttributes(queryParamCheckoutId));
      });
    }
  }, [queryParamCheckoutId, checkoutId, dispatch]);

  // LOAD EXISTING DATA FROM REDUX FOR LOGGED IN USER
  useEffect(() => {
    if (
      globalAuthState.isLoggedIn === true &&
      globalAuthState.customerId !== null &&
      queryParamCheckoutId !== null
    ) {
      dispatch(fetchExistingData(queryParamCheckoutId, globalAuthState.customerId));
    }
  }, [dispatch, globalAuthState.isLoggedIn, globalAuthState.customerId, queryParamCheckoutId]);

  // DISPLAY AND SCROLL TO ALERT BOX IF ERRORS
  useEffect(() => {
    if (hasCustomerErrors && customerSearchRef.current) {
      customerSearchRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [hasCustomerErrors]);

  const renderCustomerError = () => {
    if (globalCustomerState.errorMessage === EXISTING_CUSTOMER_ERROR) {
      return (
        <AlertSliceError
          slice={{
            errorTitle: 'There was an error with your submission',
            errorMessage: (
              <span>
                The details entered belongs to an existing customer. Please <LinkEcommerceLogin />{' '}
                to continue.
              </span>
            ),
            hasErrors: true,
          }}
        />
      );
    }

    return (
      <AlertSliceError
        slice={{
          errorTitle: 'There was an error with your submission',
          errorMessage: globalCustomerState.errorMessage || '',
          hasErrors: globalCustomerState.hasErrors,
        }}
      />
    );
  };

  return (
    <LargerHeaderWithNavLayout>
      <SCEcommerceNewPlan>
        <StepBody>
          <FormProvider {...reactHookFormCustomerIdentification}>
            <form
              onSubmit={handleSubmitCustomer(formSubmitCustomerHandler)}
              className="flex flex-col gap-8"
            >
              {authLoading || checkoutLoading ? (
                <Loader />
              ) : (
                <div className="flex flex-col gap-6">
                  <Controller
                    name="paymentDetails"
                    rules={{
                      validate: (paymentDetails) =>
                        paymentDetails.saleAmount !== 0 &&
                        paymentDetails.depositPercent !== 'N/A' &&
                        paymentDetails.paymentTerm !== 'N/A',
                    }}
                    render={({ onChange, value }) => (
                      <PaymentCalculator
                        type="ecommerce"
                        readOnly={isCustomerFormReadonly}
                        rates={merchantData.rates}
                        paymentFrequencies={PAYMENT_FREQUENCIES}
                        establishmentFees={merchantData.establishmentFees}
                        otherFees={merchantData.otherFees}
                        updatePaymentDetails={onChange}
                        paymentDetails={value}
                        repeatCustomer={repeatCustomer}
                        shouldShowAllTerms={showAllAvailableTerms}
                      />
                    )}
                  />
                  <FeeDetails
                    accountKeepingFee={merchantData.otherFees.monthlyAccountKeepingFee}
                    processingFee={merchantData.otherFees.paymentProcessingFee}
                    paymentFrequency={selectedPaymentFrequency}
                  />
                </div>
              )}

              <hr id="end-payment" />

              <div
                ref={customerSearchRef}
                style={{ position: 'relative', top: '-2em', display: 'none' }}
              />

              <EditDetailsBlock
                title={'Your Details'}
                showEditToggleButton={false}
                toggleEditing={() => {}}
                editing
                text={CUSTOMER_DETAILS_CUSTOMER_INSTRUCTIONS}
              >
                {/**
                 * Customer details (Title, First Name, Middle Name, Last Name, Email, and DOB)
                 * can be edited by customers whose IDs have not yet been verified.
                 *
                 * If the customer is not in SugarCRM yet (i.e not an existing customer) ,
                 * the user will be prompted to enter their password as well.
                 */}
                <CustomerDetails existingCustomer={isExistingCustomer} readOnly={isIdVerified} />
              </EditDetailsBlock>
              <hr id="end-customer-details" />

              {!customerMatch && (
                <div className="buttons">
                  <Button
                    colour="blue"
                    maxWidth="100%"
                    iconPosition="right"
                    disabled={customerLoading || checkoutLoading}
                    icon={customerLoading || checkoutLoading ? <SCSpinner /> : undefined}
                  >
                    {canCustomerProceed ? 'Update' : 'Continue'}
                  </Button>
                  <ButtonCancelPlan disabled={customerLoading || checkoutLoading} />
                </div>
              )}

              {hasCustomerErrors && !hasCheckoutErrors && renderCustomerError()}

              {hasCheckoutErrors && (
                <Alert
                  title="There was an error"
                  body={globalCheckoutState.errorMessage || ''}
                  outcome="error"
                  icon={<IconAttention />}
                />
              )}

              {/* Existing customer search errors - when errors is an array */}
              {Array.isArray(customerSearchErrors) &&
                customerSearchErrors.map((error) => {
                  return (
                    <Alert
                      key={error.code}
                      title={'There was an error with your submission'}
                      body={error.message}
                      outcome="error"
                      icon={<IconAttention />}
                    />
                  );
                })}

              {/*
                Existing customer search errors - when errors is an object
                Refer to case where 'Allow Further Business' is set to No on SugarCRM
              */}
              {!Array.isArray(customerSearchErrors) && customerSearchErrors && (
                <Alert
                  title={'There was an error with your submission'}
                  body={
                    'Thank you for submitting this application. Unfortunately we are unable to offer you a further purchase at this time.'
                  }
                  outcome="error"
                  icon={<IconAttention />}
                />
              )}
            </form>
          </FormProvider>

          <FormProvider {...reactHookFormDetails}>
            <div
              ref={hiddenBlockRef}
              style={{ position: 'relative', top: '-2em', display: 'none' }}
            />
            <form onSubmit={handleSubmit(formSubmitDetailsHandler)} className="flex flex-col gap-8">
              {customerMatch && (
                <>
                  <EditDetailsBlock
                    title={'Address Details'}
                    showEditToggleButton={false}
                    toggleEditing={() => {}}
                    editing
                    text={
                      isExistingCustomer
                        ? ADDRESS_DETAIL_TEXT_EXISTING_CUSTOMER
                        : ADDRESS_DETAIL_TEXT_NEW_CUSTOMER
                    }
                  >
                    <AddressDetails existingCustomer={isExistingCustomer} editing />
                  </EditDetailsBlock>
                  <hr id="end-address-details" />

                  <EditDetailsBlock
                    title={'Employment, Benefits & Income'}
                    showEditToggleButton={false}
                    toggleEditing={() => {}}
                    editing
                  >
                    <EmploymentDetails
                      existingCustomer={isExistingCustomer}
                      editing
                      allowIncomeEditing={true}
                    />
                  </EditDetailsBlock>
                  <hr id="end-employment" />

                  <EditDetailsBlock
                    title={'Identification'}
                    showEditToggleButton={false}
                    toggleEditing={() => {}}
                    editing
                    text={CUSTOMER_IDENTIFICATION}
                  >
                    <IdentificationDetails
                      handleIdentificationType={(type: IdentificationType) => {
                        clearErrors('identificationDetails');
                        setIdentificationType(type);
                      }}
                      identificationType={identificationType}
                      existingCustomer={isExistingCustomer}
                      editing
                    />
                  </EditDetailsBlock>
                  <hr id="end-identification" />

                  <div className="buttons">
                    <Button
                      withShadow
                      maxWidth="100%"
                      iconPosition="right"
                      disabled={customerLoading || checkoutLoading}
                      icon={customerLoading || checkoutLoading ? <SCSpinner /> : <IconNext />}
                    >
                      Next Step
                    </Button>
                    <ButtonCancelPlan disabled={customerLoading || checkoutLoading} />
                  </div>
                </>
              )}
            </form>
          </FormProvider>
        </StepBody>
      </SCEcommerceNewPlan>
    </LargerHeaderWithNavLayout>
  );
};

export default Step1;
